import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { syncSentryUser } from '../lib/sentry';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Try to restore user from localStorage on initial load
    const storedUser = localStorage.getItem('supabase.auth.user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        syncSentryUser(session.user);
        localStorage.setItem('supabase.auth.user', JSON.stringify(session.user));
      } else {
        setUser(null);
        syncSentryUser(null);
        localStorage.removeItem('supabase.auth.user');
      }
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        syncSentryUser(session.user);
        localStorage.setItem('supabase.auth.user', JSON.stringify(session.user));
      } else {
        setUser(null);
        syncSentryUser(null);
        localStorage.removeItem('supabase.auth.user');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Always clear client state. Remote signOut can 403 / AuthSessionMissingError when the
    // JWT is already expired or missing while a cached user is still shown in the UI.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { error } = session
        ? await supabase.auth.signOut()
        : await supabase.auth.signOut({ scope: 'local' });
      if (
        error &&
        error.name !== 'AuthSessionMissingError' &&
        !/session missing/i.test(error.message ?? '')
      ) {
        console.warn('signOut:', error.message);
      }
    } catch (err) {
      console.warn('signOut:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('supabase.auth.user');
      syncSentryUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};